import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/posthog";
import { toast } from "sonner";
import {
  Youtube, X, Loader2, Sparkles, Flag, ShieldAlert,
} from "lucide-react";

/**
 * WatchLearnPanel — Iter67 / Phase 6.
 *
 * Surfaces 1-6 short, family-safe educational clips related to the
 * lesson title from a whitelist of pedagogical + creator-economy
 * channels. Clicking a card plays the video INLINE in a YouTube
 * iframe — we never link out, so the learner can't fall into the
 * YouTube algorithm rabbit hole.
 *
 * Resilience (iter73)
 * -------------------
 * Earlier iterations cleared `results` and reset to `loading` whenever
 * the effect re-ran. That meant any parent re-render with a stable
 * topic string but a freshly-allocated parent state would briefly
 * blank the panel. We now:
 *   • Skip the fetch entirely when the topic value is identical to
 *     the last successful query (deep-string compare, not reference).
 *   • Don't clear `results` on a fresh fetch — old cards remain
 *     visible while the new ones load. Only swap them in once we
 *     have a new response. This makes the "disappears on scroll" bug
 *     impossible: even if parent state churn re-mounts the effect,
 *     the cached state stays put.
 *
 * If the backend returns 503 (no `YOUTUBE_API_KEY` configured), the
 * panel renders nothing — no broken state.
 */
export default function WatchLearnPanel({ topic, context = "", lang = "en" }) {
  const [phase, setPhase] = useState("loading"); // loading | ready | empty | hidden | dismissed
  const [results, setResults] = useState([]);
  const [matchedVia, setMatchedVia] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [reporting, setReporting] = useState(null);
  const lastQueryRef = useRef(null);

  useEffect(() => {
    const trimmed = (topic || "").trim();
    if (!trimmed || trimmed.length < 4) {
      setPhase("empty");
      lastQueryRef.current = null;
      return;
    }
    const ctx = (context || "").trim();
    const key = `${trimmed.toLowerCase()}::${ctx.toLowerCase()}::${(lang || "en").toLowerCase()}`;
    if (lastQueryRef.current === key) return;
    let cancelled = false;
    if (results.length === 0) setPhase("loading");
    api.get("/youtube/search", { params: { topic: trimmed, lang, context: ctx } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data?.results) ? data.results : [];
        lastQueryRef.current = key;
        if (!list.length) {
          setResults([]);
          setMatchedVia(null);
          setPhase("empty");
          return;
        }
        setResults(list.slice(0, 6));
        setMatchedVia(data?.matched_via || null);
        setPhase("ready");
        setPlaying(null);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 503) {
          lastQueryRef.current = key;
          setPhase("hidden");
        } else if (results.length === 0) {
          setPhase("empty");
        }
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, lang, context]);

  if (phase === "hidden" || phase === "dismissed") return null;

  return (
    <section
      className="mt-3 mb-6 glass border border-white/10 rounded-2xl overflow-hidden"
      data-testid="watch-learn-panel"
    >
      <div className="px-5 pt-4 pb-3 border-b border-white/5 flex items-center gap-3">
        <div className="h-8 w-8 grid place-items-center rounded-lg bg-rose-500/12 border border-rose-500/30 shrink-0">
          <Youtube size={15} className="text-rose-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm tracking-tight text-white">
            Watch &amp; Learn
            <span className="ml-2 text-[10px] font-display uppercase tracking-[0.16em] text-white/40 align-middle">
              Family-safe results
            </span>
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5 truncate">
            {matchedVia
              ? <>Closely related to &ldquo;{topic}&rdquo; · showing clips for <span className="text-white/70">&ldquo;{matchedVia}&rdquo;</span>.</>
              : <>Curated channels first, then the closest community videos — picked for &ldquo;{topic}&rdquo;.</>
            }
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPhase("dismissed")}
          className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5 shrink-0"
          aria-label="Hide Watch & Learn for this lesson"
          title="Hide Watch & Learn"
          data-testid="watch-learn-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {phase === "loading" && (
        <div className="py-8 grid place-items-center" data-testid="watch-learn-loading">
          <Loader2 size={20} className="animate-spin text-white/40" />
        </div>
      )}

      {phase === "empty" && (
        <div className="px-5 py-6 text-center text-white/45 text-xs flex flex-col items-center gap-2" data-testid="watch-learn-empty">
          <Sparkles size={14} />
          No matching clips from our curated channels yet — try a different lesson topic.
        </div>
      )}

      {phase === "ready" && (
        <>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {results.map((r) => (
              <article
                key={r.video_id}
                className="rounded-xl overflow-hidden bg-black/40 border border-white/10 hover:border-white/25 transition group relative"
                data-testid={`watch-learn-card-${r.video_id}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setPlaying(r);
                    trackEvent("watch_learn_play", {
                      channel: r.channel, video_id: r.video_id, topic,
                    });
                  }}
                  className="block w-full text-left"
                  data-testid={`watch-learn-play-${r.video_id}`}
                >
                  <div className="aspect-video bg-white/[0.04] relative overflow-hidden">
                    {r.thumbnail && (
                      <img
                        src={r.thumbnail}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover transition group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0" />
                    {r.whitelisted && (
                      <span
                        className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-mono-oak uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#00FF9D]/12 border border-[#00FF9D]/30 text-[#9bffcc] backdrop-blur"
                        data-testid={`watch-learn-curated-${r.video_id}`}
                        title="From our hand-picked educational channels"
                      >
                        Curated
                      </span>
                    )}
                    <div className="absolute inset-0 grid place-items-center">
                      <div className="h-11 w-11 rounded-full bg-rose-500/85 backdrop-blur grid place-items-center transition group-hover:scale-110 shadow-xl">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-2 pr-9">
                    <div className="text-[12.5px] text-white font-display leading-snug line-clamp-2" data-testid={`watch-learn-title-${r.video_id}`}>
                      {r.title}
                    </div>
                    <div className="text-[10px] text-white/45 font-mono-oak mt-1 truncate">
                      {r.channel}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setReporting(r)}
                  className="absolute bottom-2 right-2 h-6 w-6 grid place-items-center rounded-md text-white/35 hover:text-rose-300 hover:bg-white/5 z-10"
                  aria-label="Report this video"
                  title="Report inappropriate or off-topic"
                  data-testid={`watch-learn-report-${r.video_id}`}
                >
                  <Flag size={11} />
                </button>
              </article>
            ))}
          </div>

          {/* Legal disclaimer — small but visible. Protects the platform
              by making the third-party nature of the content explicit
              and surfacing the report path. */}
          <div
            className="px-5 py-3 border-t border-white/5 text-[10px] text-white/40 leading-relaxed flex items-start gap-2"
            data-testid="watch-learn-disclaimer"
          >
            <ShieldAlert size={11} className="text-white/30 shrink-0 mt-0.5" />
            <p>
              Videos are sourced from third-party YouTube channels we have curated for educational value.
              Oak AI Campus does not host, produce, or endorse this content and is not responsible for changes the
              channel owners may make after we list them. If a clip is inappropriate, off-topic, or in any way
              concerning, please <span className="text-rose-200 font-display">tap the flag icon</span> on the card —
              we review every report and remove offending channels from the whitelist. You watch at your own discretion.
            </p>
          </div>
        </>
      )}

      {/* Floating Picture-in-Picture player — Iter74. Renders into the
          document body via a portal so scroll inside the lesson sheet
          never moves the video out of view. Initial position: top-right
          of the viewport with safe insets. User can close to fully
          unmount the iframe. Single source of truth for `playing` state
          lives in this component.
          Iter77: close button moved to OVER the video as a circular
          chip, larger touch target (32px), explicit z-index above the
          iframe + stopPropagation, so it can never be swallowed by the
          YouTube iframe's own focus capture. */}
      {playing && createPortal(
        <div
          className="fixed top-4 right-4 z-[100] w-[min(360px,calc(100vw-2rem))] rounded-xl overflow-hidden border border-white/15 bg-[#0B0E14] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.8)]"
          role="dialog"
          aria-label="Floating video player"
          data-testid="watch-learn-pip"
        >
          <div className="relative aspect-video bg-black">
            <iframe
              src={`${playing.embed_url}&autoplay=1`}
              title={playing.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              referrerPolicy="strict-origin-when-cross-origin"
            />
            {/* Overlay close — floats over the iframe top-right with a
                solid black backdrop for guaranteed visibility against
                any thumbnail. z-10 puts it above the iframe element. */}
            <button
              type="button"
              onPointerDown={(e) => {
                // Catch the pointer BEFORE the iframe can absorb it.
                // onClick alone has been observed to occasionally miss
                // when the iframe steals focus on autoplay start.
                e.stopPropagation();
                e.preventDefault();
                setPlaying(null);
              }}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setPlaying(null); }}
              className="absolute top-2 right-2 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/75 hover:bg-black text-white border border-white/15 hover:border-white/40 transition shadow-lg"
              aria-label="Close player"
              data-testid="watch-learn-pip-close"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
          <div className="px-3 py-2 flex items-center gap-2 border-t border-white/8 bg-[#0B0E14]/95 backdrop-blur">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] text-white font-display line-clamp-1">{playing.title}</div>
              <div className="text-[9px] text-white/45 font-mono-oak truncate">{playing.channel}</div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setReporting(playing); }}
              className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-rose-300 hover:bg-white/5"
              aria-label="Report this video"
              title="Report"
              data-testid="watch-learn-pip-report"
            >
              <Flag size={12} />
            </button>
            {/* Secondary text close in footer too — duplicate path so
                power-users who reach for the corner can still close it
                even if the overlay X is occluded by browser PiP UI. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setPlaying(null); }}
              className="text-[11px] text-white/55 hover:text-white px-2 py-1 rounded-md hover:bg-white/5 font-display"
              aria-label="Close player"
              data-testid="watch-learn-pip-close-footer"
            >
              Close
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* Report modal — quick triage flow. Stored on the backend in
          `yt_reports`; admin reviews + adds channel IDs to the
          blocklist as needed. */}
      {reporting && createPortal(
        <div
          className="fixed inset-0 z-[110] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          data-testid="watch-learn-report-modal"
        >
          <ReportForm
            target={reporting}
            topic={topic}
            onClose={() => setReporting(null)}
            onDone={() => {
              // Also stop the player if the user is reporting the
              // currently-playing clip — they almost certainly want it gone.
              if (playing?.video_id === reporting?.video_id) setPlaying(null);
              setReporting(null);
            }}
          />
        </div>,
        document.body,
      )}
    </section>
  );
}

/** Minimal report sheet — 4 canned reasons + an optional note. */
function ReportForm({ target, topic, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const REASONS = [
    { id: "off_topic", label: "Off-topic / irrelevant to the lesson" },
    { id: "inappropriate", label: "Inappropriate content" },
    { id: "misinformation", label: "Misleading or factually wrong" },
    { id: "other", label: "Other" },
  ];
  const submit = async () => {
    if (!reason) { toast.error("Pick a reason first."); return; }
    setSubmitting(true);
    try {
      await api.post("/youtube/report", {
        video_id: target.video_id,
        channel_id: target.channel_id || null,
        reason,
        topic: topic || "",
        notes,
      });
      toast.success("Reported. We'll review and remove if needed.");
      onDone?.();
    } catch {
      toast.error("Couldn't send the report — try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="glass max-w-md w-full rounded-2xl border border-white/12 p-5" data-testid="report-form">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 grid place-items-center rounded-xl bg-rose-500/15 border border-rose-500/35 shrink-0">
          <Flag size={14} className="text-rose-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-display text-sm">Report this clip</h4>
          <p className="text-[11px] text-white/55 line-clamp-2 mt-0.5">{target.title}</p>
        </div>
        <button onClick={onClose} className="text-white/45 hover:text-white" aria-label="Close" data-testid="report-form-close">
          <X size={14} />
        </button>
      </div>
      <div className="space-y-2">
        {REASONS.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setReason(r.id)}
            className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition ${
              reason === r.id
                ? "border-rose-400/55 bg-rose-500/10 text-white"
                : "border-white/10 bg-white/[0.03] text-white/75 hover:bg-white/[0.06]"
            }`}
            data-testid={`report-reason-${r.id}`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, 500))}
        placeholder="Optional note (max 500 chars)"
        rows={2}
        className="mt-3 w-full text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/35 focus:outline-none focus:border-white/30"
        data-testid="report-notes"
      />
      <button
        type="button"
        onClick={submit}
        disabled={submitting || !reason}
        className="mt-3 w-full py-2.5 rounded-xl btn-primary font-display font-semibold text-sm disabled:opacity-50"
        data-testid="report-submit"
      >
        {submitting ? "Sending…" : "Send report"}
      </button>
    </div>
  );
}
